import React from "react";
import Permission from "./PermissionType";
import { ACCOUNT_OWNER, MULTIPLE_PERMISSIONS } from "src/constants/roles";
import { CLIENT_NOTICEBOARD, FULL_ACCESS_BUSINESS_CHAT, LEADERSHIP_ROLES } from "src/constants/permissions";
import {
    PermissionForm,
    PermissionScrollGrid,
    PermissionFormRow,
    PermissionSelectedRole,
    PermissionGrid,
    PermissionCheckField,
    PermissionRadioField,
    PermissionSubText,
    PermissionModalRowLabel
} from "src/components";
import { FEATURE_FLAGS, hasFeature } from "src/constants/FeatureFlag";
import {
    buildPermissionLookup,
    getDisplaySections,
    isPermissionDisplayChecked,
    isPermissionDisplayDisabled
} from "./permissionPresentation";


const RolesForm = (props) => {
    const { isClient, roles, userRole, isUserRole, clickRole, disableInput, currentSelectedRole,
        permissions, currentSelectedPermissions,
        disableChatPermission, hasLeadershipRole, setPermissionIsChecked, customPermissionLabels, hiddenPermissionIds } = props;
    const canViewChat = hasFeature(props.user, FEATURE_FLAGS.BUSINESS_CHAT);

    return (
        <>
            { 
                <PermissionForm>
                    <>
                        <hr />
                        <RolesContainer 
                            isClient={isClient} 
                            roles={roles}
                            userRole={userRole} 
                            isUserRole={isUserRole} 
                            clickRole={clickRole} 
                            disableInput={disableInput} 
                            currentSelectedRole={currentSelectedRole} 
                            hasLeadershipRole={hasLeadershipRole}
                        />
                    </>
                
                    { userRole !== ACCOUNT_OWNER && 
                        <>
                            <hr style={{ marginRight: 30 }}/>
                            {isClient ? (
                                <PermissionScrollGrid
                                    style={{ 
                                        display: "grid", gridTemplateColumns: "repeat(2, 1fr)",
                                    }}
                                >
                                    <div className="col">
                                        {canViewChat && (
                                            <ChatPermission
                                                permissions={permissions}
                                                currentSelectedPermissions={currentSelectedPermissions}
                                                setPermissionIsChecked={setPermissionIsChecked}
                                                disableChatPermission={disableChatPermission}
                                                customPermissionLabels={customPermissionLabels}
                                                hiddenPermissionIds={hiddenPermissionIds}
                                            />
                                        )}
                                    </div>

                                    <div className="col">
                                        <ClientNoticeboardPermission
                                            permissions={permissions}
                                            currentSelectedPermissions={currentSelectedPermissions}
                                            setPermissionIsChecked={setPermissionIsChecked}
                                            disableInput={disableInput}
                                            hiddenPermissionIds={hiddenPermissionIds}
                                        />
                                    </div>
                                </PermissionScrollGrid>
                            ) : (
                                <PermissionScrollGrid
                                    style={{ 
                                        display: "grid", gridTemplateColumns: canViewChat ? "repeat(2, 1fr)" : "repeat(2, 1fr)",
                                    }}
                                >
                                    <div className="col">
                                        { permissions.app && <AppPermission
                                            permissions={permissions}
                                            currentSelectedPermissions={currentSelectedPermissions}
                                            setPermissionIsChecked={setPermissionIsChecked}
                                            disableInput={disableInput}
                                            customPermissionLabels={customPermissionLabels}
                                            hiddenPermissionIds={hiddenPermissionIds}
                                        />
                                        }
                                        {canViewChat && (
                                            <ChatPermission
                                                permissions={permissions}
                                                currentSelectedPermissions={currentSelectedPermissions}
                                                setPermissionIsChecked={setPermissionIsChecked}
                                                disableChatPermission={disableChatPermission}
                                                customPermissionLabels={customPermissionLabels}
                                                hiddenPermissionIds={hiddenPermissionIds}
                                            />
                                        )}
                                    </div>

                                    <div className="col">
                                        <WebPermission
                                            permissions={permissions}
                                            currentSelectedPermissions={currentSelectedPermissions}
                                            setPermissionIsChecked={setPermissionIsChecked}
                                            disableInput={disableInput}
                                            customPermissionLabels={customPermissionLabels}
                                            hiddenPermissionIds={hiddenPermissionIds}
                                        />
                                    </div>
                                </PermissionScrollGrid>
                            )}
                        </>
                    }
                </PermissionForm>
            }
        </>
    );
};

const Checkbox = (props) => {
    const { item, isChecked, setPermissionIsChecked } = props;

    return (
        <PermissionCheckField hidden={props.hidden} depth={item.depth} disabled={props.disabled}>
            <input type="checkbox" className="permissions-checkbox" value={item.id}
                checked={ isChecked }
                onChange={() => setPermissionIsChecked(item.selectionId, !isChecked)}
                disabled={props.disabled}
            />
            <label>{ item.name }</label>
            <PermissionSubText>
                { item.description }
            </PermissionSubText>
        </PermissionCheckField>
    );
};

const RolesContainer = (props) => {
    const { isClient, roles, userRole, isUserRole, clickRole, disableInput, currentSelectedRole, hasLeadershipRole } = props;

    return (
        <div style={{ margin: "1em 30px 1em 0" }}>
            <PermissionModalRowLabel>Roles</PermissionModalRowLabel>
            <PermissionFormRow>

                { (!isClient && userRole !== ACCOUNT_OWNER) && 
                    <PermissionGrid>
                        {   
                            roles && roles.map((role, index) => {
                                const roleInputId = `permission-role-${role.id}`;
                                return (
                                // hide the Account Owner role if selected user is not an account owner
                                // also hide "Multiple Permissions" role
                                    (userRole !== ACCOUNT_OWNER && role.id === ACCOUNT_OWNER) || role.id === MULTIPLE_PERMISSIONS ? ""
                                        :
                                        <PermissionRadioField key={index}>
                                            <input id={roleInputId} type="checkbox" name="role" value={role.id}
                                                checked={ isUserRole(role.id) }
                                                onChange={() => clickRole(role.id)}
                                                disabled={disableInput}
                                            />
                                            <label htmlFor={roleInputId}>{ role.name }</label>
                                        </PermissionRadioField>
                                );
                            })
                        }
                    </PermissionGrid>
                }
                <PermissionSelectedRole $alignStart={isClient}>
                    <Permission role={currentSelectedRole} isClient={isClient} hasLeadershipRole={hasLeadershipRole}></Permission>
                </PermissionSelectedRole>
            </PermissionFormRow>
        </div>
    );
};

const AppPermission = (props) => {
    const { permissions, currentSelectedPermissions, setPermissionIsChecked, disableInput, customPermissionLabels, hiddenPermissionIds } = props;
    const section = getDisplaySections(permissions, ["app"], customPermissionLabels, hiddenPermissionIds)[0];

    return (
        <>
            <PermissionModalRowLabel>{section?.title}</PermissionModalRowLabel>
            {
                section?.items.map((item) => {
                    return (
                        <Checkbox
                            key={`${item.parentId || "root"}-${item.id}`}
                            item={item}
                            hidden={false}
                            setPermissionIsChecked={setPermissionIsChecked}
                            isChecked={isPermissionDisplayChecked(item, currentSelectedPermissions)}
                            disabled={isPermissionDisplayDisabled(item, currentSelectedPermissions, disableInput)}
                        />
                    );
                })
            }
        </>
    );
};

const ChatPermission = (props) => {
    const { permissions, currentSelectedPermissions, setPermissionIsChecked, disableChatPermission, customPermissionLabels, hiddenPermissionIds } = props;
    const isFullChatAccess = currentSelectedPermissions.includes(FULL_ACCESS_BUSINESS_CHAT);
    const section = getDisplaySections(permissions, ["chat"], customPermissionLabels, hiddenPermissionIds)[0];

    return (
        <div>
            <PermissionModalRowLabel>{section?.title}</PermissionModalRowLabel>
            {
                section?.items.map(item => {
                    const isLeadershipPermission = item.id === LEADERSHIP_ROLES;

                    return (
                        <Checkbox
                            key={item.id}
                            item={item}
                            setPermissionIsChecked={setPermissionIsChecked}
                            hidden={false}
                            isChecked={
                                isLeadershipPermission
                                    ? currentSelectedPermissions.includes(item.id)
                                    : currentSelectedPermissions.includes(item.id) || isFullChatAccess
                            }
                            disabled={disableChatPermission(item.id)}
                        />
                    );
                })
            }
        </div>
    );
};

const ClientNoticeboardPermission = (props) => {
    const { permissions, currentSelectedPermissions, setPermissionIsChecked, disableInput, hiddenPermissionIds } = props;
    const permissionLookup = buildPermissionLookup(permissions);

    if (!permissionLookup[CLIENT_NOTICEBOARD] || hiddenPermissionIds?.includes(CLIENT_NOTICEBOARD)) {
        return null;
    }

    const item = {
        ...permissionLookup[CLIENT_NOTICEBOARD],
        id: CLIENT_NOTICEBOARD,
        name: "Noticeboard",
        description: "View and post messages, images and video to the Clients Noticeboard Feed.",
        depth: 0,
        selectionId: CLIENT_NOTICEBOARD
    };

    return (
        <>
            <PermissionModalRowLabel>Noticeboard (App & Web)</PermissionModalRowLabel>
            <Checkbox
                key={CLIENT_NOTICEBOARD}
                item={item}
                setPermissionIsChecked={setPermissionIsChecked}
                hidden={false}
                isChecked={isPermissionDisplayChecked(item, currentSelectedPermissions)}
                disabled={disableInput}
            />
        </>
    );
};

const WebPermission = (props) => {
    const { permissions, currentSelectedPermissions, setPermissionIsChecked, disableInput, customPermissionLabels, hiddenPermissionIds } = props;
    const section = getDisplaySections(permissions, ["web"], customPermissionLabels, hiddenPermissionIds)[0];

    return (
        <>
            <PermissionModalRowLabel>{section?.title}</PermissionModalRowLabel>
            {
                section?.items.map((item) => {
                    const hidden = false; // !IsFeatureReady.emailCampaigns() && item.name.includes("Email Campaigns");
                    return (
                        <Checkbox
                            key={`${item.parentId || "root"}-${item.id}`}
                            hidden={hidden}
                            item={item}
                            setPermissionIsChecked={setPermissionIsChecked}
                            isChecked={isPermissionDisplayChecked(item, currentSelectedPermissions)}
                            disabled={isPermissionDisplayDisabled(item, currentSelectedPermissions, disableInput)}
                        />
                    );
                })
            }

        </>
    );
};

export default RolesForm;
